import { getProp } from "../ParsedSummary";
import { teamworkLevelLabel } from "utils/teamworkLevel";
import { answerSurveyRows, surveyByStudentName } from "utils/answerSurveyTable";

/**
 * Question Detail, in two shapes.
 *
 * A question's detail is read as a SEQUENCE, and the two sides of the
 * deterministic gate sequence it differently: a pre-cutoff report keeps the
 * order and the bullets it has always had, while a gated report opens with what
 * happened and drops the two bullets the question visual above it already
 * shows. An ordering difference cannot be expressed as conditionals inside one
 * walk without making that walk unreadable, so the two orders are written out
 * as `legacyQuestionDetail` and `gatedQuestionDetail` and picked between ONCE,
 * from `deterministic.enabled`. Every leaf either sequence shares is a helper
 * above them; only the sequence itself is written twice.
 *
 * These two are intended to become two modules. Keep them separable.
 */

const parseQuestionText = (text) => {
  if (!text || typeof text !== 'string') return { text: '', imageUrl: null };

  // Match markdown image syntax: ![alt text](url)
  const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/;
  const match = text.match(imageRegex);

  if (match) {
    const imageUrl = match[2];
    const textWithoutImage = text.replace(imageRegex, '').trim();
    return { text: textWithoutImage, imageUrl };
  }

  return { text, imageUrl: null };
};

const collaborationLevelLabels = {
  '1': 'Needs Intervention (Free Agents)',
  '2': 'Needs Guidance (Practice Squad)',
  '3': 'Effective Collaboration (Big Leagues)',
  '4': 'Exceeding Expectations (Dream Team)'
};

export const collaborationLevelDescriptions = {
  '1': "Is everyone on a solo mission? At this level, it's pretty quiet, or maybe one person is running the show. You may be sharing final answers but keeping the awesome thinking behind them a secret.",
  '2': "Okay, practice is on! So team members are starting to talk strategy by sharing the \"how\"—the steps you took to get your answer. You're building the foundation for some major math plays. You're on your way!",
  '3': "Welcome to the Big Leagues! Your whole team is in the game, and the conversation is getting deep. The game-changer here is explaining the \"why\"—the math brainpower behind your moves. You're asking sharp questions that get everyone thinking and sharing.",
  '4': "Your squad is officially a Dream Team! You're not just talking; you're building on each other's ideas to power up your thinking. Multiple players are dropping knowledge by explaining the \"why\". When tough questions pop up, you solve them as a group and support any teammate who's stuck, because you know that's how everyone learns and gets stronger. You are an unstoppable force!"
};

// Inside the gate the verdict reads as one plain sentence about the group,
// not as the internal name for it. A pre-cutoff report keeps the name it has
// always printed.
const GROUP_ALIGNED_LABEL = 'Group Aligned on Correct Answer:';
const REACHED_CONSENSUS_LABEL = 'Reached Correct Consensus:';

const TOP_QUOTE_LABEL = 'Top Quote:';
const CATALYST_QUOTE_LABEL = 'Catalyst Quote:';

// What a question says when the backend has no collaboration score for it. An
// unscored question does not mean the group agreed in Solo Time, so the copy
// claims nothing beyond the missing score.
const NO_SCORE_COPY = 'No Collaboration score was calculated for this question.';

// The number a question was written with, when what was written IS a number.
//
// `questionNumber` is free text off the model: a bare `4`, a `"Q4"`, nothing at
// all under v9, and — in roughly one report in six — the question's own UUID.
// Reading digits out of whatever arrived turned `88fb0468-...` into 88, which
// sorted the questions by nonsense, missed every deterministic map, and headed
// one of them "Question 88fb0468-5f7e-4485-bd95-3a180c23a115".
//
// The anchors are the whole point: the number has to be the WHOLE value, so an
// id is not one. The prefixes are there so this still reads everything the old
// digit-scrape read correctly — "Question 2", "#2", "2." — and gives up only
// on what was never a number.
//
// Null means the question carries no usable number and its position stands in.
export const questionNumberFrom = (raw) => {
  if (typeof raw === 'number') return Number.isInteger(raw) && raw > 0 ? raw : null;
  const match = typeof raw === 'string'
    ? raw.match(/^\s*(?:question\s*|q\s*|#\s*)?(\d+)\s*[.)]?\s*$/i)
    : null;
  return match ? Number(match[1]) : null;
};

// The key into the per-question deterministic maps, which are built from the
// challenge rows and keyed by presentation position (1-based).
const questionMapKey = (questionNumber, index) =>
  questionNumberFrom(questionNumber) ?? index + 1;

// The per-question collaboration level the students saw, keyed by question
// number. Level 0 means Team Time never happened, which shows as
// not-applicable rather than as a score of zero.
const backendRowFor = (deterministic, questionNumber, index) => {
  const levels = deterministic.questionLevels;
  if (!Array.isArray(levels)) return undefined;
  return levels[questionMapKey(questionNumber, index) - 1] ?? levels[index];
};

// The question's recorded submissions. Numbered lookup first, position as the
// fallback — the same two steps backendRowFor takes, so a payload whose
// numbering the map does not recognise degrades to presentation order instead
// of dropping the Answer & Survey table.
const submissionsFor = (deterministic, questionNumber, index) => {
  const byQuestion = deterministic.submissionsByQuestion;
  if (!byQuestion) return [];
  return byQuestion[questionMapKey(questionNumber, index)]
    ?? byQuestion[index + 1]
    ?? [];
};

// The graded answer off the challenge row: the correct option for a multiple
// choice, the solution for an open response. Undefined when the row carries
// neither — the caller falls back to the LLM's value.
const deterministicCorrectAnswer = (challenge) => {
  if (!challenge) return undefined;
  const { answer_options: options, solution_index: solutionIndex } = challenge;
  if (Array.isArray(options) && Number.isInteger(solutionIndex) && options[solutionIndex] != null) {
    return String(options[solutionIndex]);
  }
  return typeof challenge.solution === 'string' && challenge.solution
    ? challenge.solution
    : undefined;
};

// Which round the group settled on the correct answer in, from the recorded
// rounds: the earliest round where every student who answered it ended on the
// right answer. A student's last attempt in a round is their settled answer.
// Null when no round shows convergence — the verdict then stands unqualified.
const consensusRound = (submissions) => {
  if (!Array.isArray(submissions) || submissions.length === 0) return null;

  const settled = (type, number) => {
    const answers = submissions
      .filter((sub) => type !== 'solo' || !sub?.not_applicable)
      .map((sub) => {
        const inRound = (sub?.rounds || []).filter(
          (r) => r?.round_type === type && (number === undefined || r?.round_number === number),
        );
        return inRound[inRound.length - 1];
      })
      .filter(Boolean);
    return answers.length > 0 && answers.every((r) => r.is_correct === true);
  };

  if (settled('solo')) return 'Solo Time';
  for (let round = 1; round <= 4; round += 1) {
    if (settled('team', round)) return `TT Round ${round}`;
  }
  return null;
};

const backendLevelFor = (deterministic, questionNumber, index) =>
  backendRowFor(deterministic, questionNumber, index)?.collaboration_level;

// ---------------------------------------------------------------------------
// Leaves. Each pushes at most one part of a question's detail and knows nothing
// about where in the sequence it sits. Both sequences below share these.
// ---------------------------------------------------------------------------

// Where a question sits in the session, from whatever its number is written
// as. Prompts write it both as a bare `2` and as `"Q2"`. A question with no
// number at all sorts last rather than ahead of Q1, which is where NaN-as-0
// used to put it.
const questionOrder = (question) => {
  const raw = getProp(question, ['questionNumber', 'number', 'question_number']);
  return questionNumberFrom(raw) ?? Number.MAX_SAFE_INTEGER;
};

// The heading counts the question, so it prints a NUMBER — never the string the
// model happened to write there. A question the model numbered unusably is
// headed by the slide it is on, which is the number the carousel above it is
// already showing.
const pushHeading = (sections, questionNumber, index) => {
  sections.push({
    type: 'title-hr',
    content: `Question ${questionNumberFrom(questionNumber) ?? index + 1}`
  });
};

// Headline (Analysis) — check both root level and inside analysis_components.
const pushHeadline = (sections, question) => {
  const analysisComponents = getProp(question, ['analysis_components', 'analysisComponents']);
  const analysis = getProp(analysisComponents, 'headline')
    || getProp(question, ['analysis', 'headline']);
  if (analysis && typeof analysis === 'string') {
    sections.push({
      type: 'bullet',
      label: 'Headline:',
      content: analysis
    });
  }
};

const pushStandard = (sections, standard) => {
  if (standard && typeof standard === 'string') {
    sections.push({
      type: 'bullet',
      label: 'Standard:',
      content: standard
    });
  }
};

const pushImage = (sections, image) => {
  if (image) {
    sections.push({
      type: 'image',
      content: image
    });
  }
};

const pushRecommendation = (sections, question) => {
  const recommendation = getProp(question, 'recommendation');
  if (recommendation && typeof recommendation === 'string') {
    sections.push({
      type: 'bullet',
      label: 'Recommendation:',
      content: recommendation
    });
  }
};

/**
 * The verdict the model wrote, refined by the rounds it described.
 *
 * @returns {string|null} Null when the model wrote no verdict at all, which is
 *   every v9 payload — the gated sequence then asks the challenge row instead.
 */
const llmConsensusContent = (question, correctAnswer) => {
  const consensus = getProp(question, ['reachedCorrectConsensus', 'consensus', 'reached']);
  if (typeof consensus !== 'string' || !consensus) return null;

  // Only calculate round if consensus was actually reached (starts with "Yes" or is "true")
  const consensusLower = consensus.toLowerCase();
  const consensusReached = consensusLower.startsWith('yes') || consensusLower === 'true';

  // Check student answers to determine which round consensus was reached
  const studentAnswers = getProp(question, ['studentAnswers', 'student_answers', 'answers']);
  if (!consensusReached || !Array.isArray(studentAnswers) || studentAnswers.length === 0) {
    return consensus;
  }

  // Helper to check if answer is correct - checks for "(correct)" suffix or matches correctAnswer
  const isCorrectAnswer = (value) => {
    if (!value) return false;
    const str = String(value).trim().toLowerCase();
    // Check if answer has "(correct)" marker
    if (str.includes('(correct)')) return true;
    // Fallback: compare with correctAnswer if available
    if (correctAnswer) {
      const correctStr = String(correctAnswer).trim().toLowerCase();
      return str === correctStr || str.startsWith(correctStr);
    }
    return false;
  };

  // Helper to check if all students got correct answer in a specific round
  const allCorrectInRound = (roundKey, altKeys) => {
    const keys = [roundKey, ...altKeys];
    return studentAnswers.every(answer => isCorrectAnswer(getProp(answer, keys)));
  };

  // Helper to check if any student has data for a specific round
  const hasRoundData = (roundKey, altKeys) => {
    const keys = [roundKey, ...altKeys];
    return studentAnswers.some(answer => {
      const value = getProp(answer, keys);
      return value !== undefined && value !== null;
    });
  };

  // Check rounds 1-4 in order, find first round where all got correct
  if (allCorrectInRound('ttr1', ['team_round_1', 'team1'])) {
    return 'Yes (TT Round 1)';
  }
  if (hasRoundData('ttr2', ['team_round_2', 'team2']) && allCorrectInRound('ttr2', ['team_round_2', 'team2'])) {
    return 'Yes (TT Round 2)';
  }
  if (hasRoundData('ttr3', ['team_round_3', 'team3']) && allCorrectInRound('ttr3', ['team_round_3', 'team3'])) {
    return 'Yes (TT Round 3)';
  }
  if (hasRoundData('ttr4', ['team_round_4', 'team4']) && allCorrectInRound('ttr4', ['team_round_4', 'team4'])) {
    return 'Yes (TT Round 4)';
  }
  return consensus;
};

/**
 * The verdict off the challenge row, for the v9 payloads that write none.
 *
 * `null` on the row means no record exists, which is not the same as "No" and
 * is never asserted as a failure.
 *
 * @returns {string|null} "Yes (TT Round 2)", "No", or null for no record.
 */
const backendConsensusContent = (deterministic, backendRow, questionNumber, index) => {
  const aligned = backendRow?.proficiency_aligned;
  if (typeof aligned !== 'boolean') return null;
  if (!aligned) return 'No';

  // The round the group converged in, read from the recorded rounds —
  // "Yes (TT Round 2)" — with the bare verdict when no round shows it.
  const round = consensusRound(submissionsFor(deterministic, questionNumber, index));
  return `Yes${round ? ` (${round})` : ''}`;
};

const pushConsensus = (sections, label, content) => {
  if (content) {
    sections.push({
      type: 'bullet',
      label,
      content
    });
  }
};

const pushStudentAnswers = (sections, studentAnswers, correctAnswer) => {
  if (!Array.isArray(studentAnswers) || studentAnswers.length === 0) return;

  sections.push({
    type: 'bullet',
    label: 'Student Answers:',
    content: ''
  });

  // Helper to check if value is empty or null-like
  const isEmptyAnswer = (value) => {
    if (!value) return true;
    const str = String(value).trim().toLowerCase();
    return str === '' || str === 'null' || str === 'undefined';
  };

  // Helper to get display text for an answer
  const getAnswerText = (value) => {
    if (isEmptyAnswer(value)) return 'No Option Selected';
    return String(value);
  };

  // Helper to check if answer is correct (skip if status already present or empty)
  const getAnswerStatus = (answer) => {
    if (!answer || !correctAnswer || isEmptyAnswer(answer)) return '';
    const answerStr = String(answer).trim().toLowerCase();
    // Skip if answer already contains status
    if (answerStr.includes('(correct)') || answerStr.includes('(incorrect)')) return '';
    const correctStr = String(correctAnswer).trim().toLowerCase();
    return answerStr === correctStr ? ' (correct)' : ' (incorrect)';
  };

  const pushRound = (label, value) => {
    if (value === undefined || value === null) return;
    sections.push({
      type: 'sub-sub-bullet',
      label,
      content: `${getAnswerText(value)}${getAnswerStatus(value)}`
    });
  };

  studentAnswers.forEach(answer => {
    const studentName = getProp(answer, ['studentName', 'name', 'student']);
    if (!studentName || typeof studentName !== 'string') return;

    sections.push({
      type: 'sub-bullet',
      label: `${studentName}:`,
      content: ''
    });

    const solo = getProp(answer, ['solo', 'solo_result']);
    sections.push({
      type: 'sub-sub-bullet',
      label: 'Solo:',
      content: `${getAnswerText(solo)}${getAnswerStatus(solo)}`
    });

    pushRound('TTR1:', getProp(answer, ['ttr1', 'team_round_1', 'team1']));
    pushRound('TTR2:', getProp(answer, ['ttr2', 'team_round_2', 'team2']));
    pushRound('TTR3:', getProp(answer, ['ttr3', 'team_round_3', 'team3']));
    pushRound('TTR4:', getProp(answer, ['ttr4', 'team_round_4', 'team4']));
  });
};

// The model writes "null" as a string as readily as it omits a field.
const isNullishQuote = (value) => !value || String(value).trim().toLowerCase() === 'null';

const quoteAttribution = (student) =>
  student && typeof student === 'string' && !isNullishQuote(student) ? `By ${student}, ` : '';

// ---------------------------------------------------------------------------
// Leaves belonging to ONE sequence.
// ---------------------------------------------------------------------------

// Pre-cutoff: the question's text, quoted back as its own bullet.
const pushQuestionTextBullet = (sections, questionText) => {
  if (questionText) {
    sections.push({
      type: 'bullet',
      label: 'Question Text:',
      content: `"${questionText}"`
    });
  }
};

// Pre-cutoff: the graded answer, as its own bullet.
const pushCorrectAnswerBullet = (sections, correctAnswer) => {
  if (correctAnswer && typeof correctAnswer === 'string') {
    sections.push({
      type: 'bullet',
      label: 'Correct Answer:',
      content: correctAnswer
    });
  }
};

// Pre-cutoff: the LLM's Solo Time survey answers, as a prose list.
const pushSoloSurveyResults = (sections, surveyResults) => {
  if (!Array.isArray(surveyResults) || surveyResults.length === 0) return;

  sections.push({
    type: 'bullet',
    label: 'Solo Survey Results:',
    content: ''
  });

  surveyResults.forEach(survey => {
    const studentName = getProp(survey, ['studentName', 'name', 'student']);
    const response = getProp(survey, 'response');
    if (studentName && typeof studentName === 'string') {
      const responseText = typeof response === 'string' ? response : 'No response';
      sections.push({
        type: 'sub-bullet',
        label: `${studentName}:`,
        content: `"${responseText}"`
      });
    }
  });
};

// Pre-cutoff: the LLM's own collaboration block. Inside the gate the backend's
// Collaboration Level bullet replaces it.
const pushMathDiscourse = (sections, question) => {
  const collaboration = getProp(question, 'collaboration');
  if (!collaboration) return;

  sections.push({
    type: 'bullet',
    label: 'Math Discourse & Collaboration:',
    content: ''
  });

  const level = getProp(collaboration, ['level', 'category']);
  if (level) {
    const levelNum = String(level).match(/\d+/)?.[0] || level;
    // Extract description from level string if present (e.g. "Level 4: Exceeding Expectations (Dream Team)")
    const descMatch = String(level).match(/Level\s+\d+\s*[:\-]\s*(.+)/i);
    const description = descMatch ? descMatch[1] : collaborationLevelLabels[String(levelNum)];
    sections.push({
      type: 'sub-bullet',
      label: `Level ${levelNum}:`,
      content: description || '',
      tooltip: collaborationLevelDescriptions[String(levelNum)]
    });
  }

  const headline = getProp(collaboration, ['teacherFacingCommentary', 'headline']);
  if (headline && typeof headline === 'string') {
    sections.push({
      type: 'sub-bullet',
      label: 'Headline:',
      content: headline
    });
  }
};

/**
 * Pre-cutoff: the question's quote is the Catalyst Quote, ending in the model's
 * reasoning about why the line mattered.
 */
const pushLegacyQuote = (sections, question) => {
  const catalystQuote = getProp(question, ['catalystQuote', 'catalyst']);
  if (!catalystQuote) return;

  const quote = getProp(catalystQuote, 'quote');
  if (isNullishQuote(quote)) {
    sections.push({ type: 'bullet', label: CATALYST_QUOTE_LABEL, content: 'No Quotes' });
    return;
  }

  const reasoning = getProp(catalystQuote, ['reasoning', 'commentary']);
  const commentary = isNullishQuote(reasoning) ? '' : ` ${reasoning}`;
  sections.push({
    type: 'bullet',
    label: CATALYST_QUOTE_LABEL,
    content: `${quoteAttribution(getProp(catalystQuote, 'student'))}"${quote}"${commentary}`
  });
};

/**
 * Inside the gate: one line, who said it and what they said.
 *
 * The prompt writes `topQuote`; `catalystQuote` is the shape it wrote before
 * the rename and is still what production emits, so both are read here — the
 * prompt lives in Langfuse and lands on its own schedule.
 */
const pushGatedQuote = (sections, question) => {
  const topQuote = getProp(question, ['topQuote', 'catalystQuote', 'catalyst']);
  if (!topQuote) return;

  const quote = getProp(topQuote, 'quote');
  if (isNullishQuote(quote)) {
    sections.push({ type: 'bullet', label: TOP_QUOTE_LABEL, content: 'No Quotes' });
    return;
  }

  sections.push({
    type: 'bullet',
    label: TOP_QUOTE_LABEL,
    content: `${quoteAttribution(getProp(topQuote, 'student'))}"${quote}"`
  });
};

// Inside the gate: a question OKO supplied itself, once the group had worked
// through everything the teacher selected.
const pushFallbackReason = (sections, deterministic, questionNumber, index) => {
  if (!deterministic.fallbackQuestions?.has(questionMapKey(questionNumber, index))) return;

  sections.push({
    type: 'bullet',
    label: 'Why this question:',
    content: 'OKO provided this question to keep the group learning after '
      + 'they completed all available questions for your selection.'
  });
};

// A narrative is a sentence a teacher reads, or it is nothing: the prompt lives
// in Langfuse and ships without this repo, so a key of any other type is dropped
// rather than rendered.
const collaborationNarrativeOf = (question) => {
  const narrative = getProp(question, ['collaborationNarrative', 'collaboration_narrative']);
  return typeof narrative === 'string' ? narrative : '';
};

// The v9/v10 field rules have the model write "Not applicable" into
// collaborationNarrative for an unscored question. The no-score bullet already
// covers it, so a narrative that only says that is dropped; anything else it
// wrote is kept.
const saysNotApplicable = (narrative) => /^not applicable\b/i.test(narrative.trim());

// No level bullet to carry it: the narrative still stands on its own rather
// than being lost.
const pushNarrativeAlone = (sections, narrative) => {
  if (!narrative) return;
  sections.push({
    type: 'bullet',
    content: narrative
  });
};

const pushNoScoreLevel = (sections, narrative) => {
  sections.push({
    type: 'bullet',
    label: 'Collaboration Level:',
    content: NO_SCORE_COPY
  });
  if (saysNotApplicable(narrative)) return;
  pushNarrativeAlone(sections, narrative);
};

// Inside the gate: one bullet carrying the backend's collaboration level, its
// tooltip, and the LLM's narrative as the sentence after it — the authorship
// split the v9 contract describes, read as a single line.
const pushTeamworkLevel = (sections, question, deterministic, questionNumber, index) => {
  const backendLevel = backendLevelFor(deterministic, questionNumber, index);
  const narrative = collaborationNarrativeOf(question);

  if (backendLevel === 0 || backendLevel === null) {
    pushNoScoreLevel(sections, narrative);
    return;
  }

  const label = teamworkLevelLabel(backendLevel);
  if (!label) {
    pushNarrativeAlone(sections, narrative);
    return;
  }

  sections.push({
    type: 'bullet',
    label,
    content: narrative,
    tooltip: collaborationLevelDescriptions[String(backendLevel)]
  });
};

// Inside the gate: the standard is read off the challenge row, because v9
// stopped writing it — a List[str], because a question may be attributed to
// several standards and none of them may be dropped in favour of another.
//
// `standard_labels` names each code the way the Session Summary's Focus line
// names the session's own, resolved from the same backend map, so a teacher
// never reads "6.G.A.2" here and its full name there. Older rows carry no
// labels; those fall back to the bare codes.
const gatedStandard = (question, backendRow) => {
  const backendLabels = backendRow?.standard_labels;
  const namedStandards = Array.isArray(backendLabels) && backendLabels.length > 0
    ? backendLabels
    : backendRow?.challenge?.standards;
  return Array.isArray(namedStandards) && namedStandards.length > 0
    ? namedStandards.join(', ')
    : getProp(question, ['standard', 'alignment']);
};

// ---------------------------------------------------------------------------
// The two sequences.
// ---------------------------------------------------------------------------

/**
 * Question Detail with the gate SHUT: what a pre-cutoff session has always
 * rendered, in the order it has always rendered it. Nothing here reads the
 * deterministic data, because a closed gate supplies none.
 */
const legacyQuestionDetail = (sections, question, index) => {
  const questionNumber = getProp(question, ['questionNumber', 'number', 'question_number']);
  const correctAnswer = getProp(question, ['correctAnswer', 'correct', 'answer']);
  const questionTextRaw = getProp(question, ['questionText', 'text', 'content', 'question_content']);
  const { text: questionText, imageUrl } = typeof questionTextRaw === 'string'
    ? parseQuestionText(questionTextRaw)
    : { text: '', imageUrl: null };

  pushHeading(sections, questionNumber, index);
  pushStandard(sections, getProp(question, ['standard', 'alignment']));
  if (questionTextRaw && typeof questionTextRaw === 'string') {
    pushQuestionTextBullet(sections, questionText);
    pushImage(sections, imageUrl);
  }
  pushCorrectAnswerBullet(sections, correctAnswer);
  pushConsensus(
    sections,
    REACHED_CONSENSUS_LABEL,
    llmConsensusContent(question, correctAnswer),
  );
  pushHeadline(sections, question);
  pushRecommendation(sections, question);
  pushSoloSurveyResults(sections, getProp(question, ['surveyResults', 'survey']));
  pushStudentAnswers(
    sections,
    getProp(question, ['studentAnswers', 'student_answers', 'answers']),
    correctAnswer,
  );
  pushMathDiscourse(sections, question);
  pushLegacyQuote(sections, question);
};

/**
 * Question Detail with the gate OPEN: the teacher reads a question top-down —
 * what happened, whether the group got there, what it was assessing, how they
 * worked, the line worth quoting, what to do next, then the raw answers. The
 * question's text, image and graded answer are not repeated in prose; the
 * question visual above the detail shows all three.
 */
const gatedQuestionDetail = (sections, question, index, deterministic) => {
  const questionNumber = getProp(question, ['questionNumber', 'number', 'question_number']);
  const backendRow = backendRowFor(deterministic, questionNumber, index);

  // The graded answer is still read, though it is no longer a bullet: the
  // answer list and the consensus verdict both grade against it.
  const correctAnswer = deterministicCorrectAnswer(backendRow?.challenge)
    || getProp(question, ['correctAnswer', 'correct', 'answer']);

  const surveyResults = getProp(question, ['surveyResults', 'survey']);
  const tableRows = answerSurveyRows(
    submissionsFor(deterministic, questionNumber, index),
    surveyByStudentName(surveyResults),
  );

  pushHeading(sections, questionNumber, index);
  pushHeadline(sections, question);
  pushConsensus(
    sections,
    GROUP_ALIGNED_LABEL,
    llmConsensusContent(question, correctAnswer)
      || backendConsensusContent(deterministic, backendRow, questionNumber, index),
  );
  pushStandard(sections, gatedStandard(question, backendRow));
  pushFallbackReason(sections, deterministic, questionNumber, index);
  pushTeamworkLevel(sections, question, deterministic, questionNumber, index);
  pushGatedQuote(sections, question);
  pushRecommendation(sections, question);

  if (tableRows.length > 0) {
    sections.push({
      type: 'answer-survey-table',
      rows: tableRows,
    });
  } else {
    // The table supersedes the prose answer list; without one, the list stays.
    pushStudentAnswers(
      sections,
      getProp(question, ['studentAnswers', 'student_answers', 'answers']),
      correctAnswer,
    );
  }
};

/**
 * The questions the report describes.
 *
 * How MANY questions there are is a layout question, and is not the same as
 * which of them the backend scored — `deriveQuestionItems` answers that, and
 * correctly answers "none" for a v9 payload, which writes no per-question facts.
 *
 * @param {object} summary The parsed report.
 * @returns {Array<object>} Empty when the report describes no questions.
 */
export const getQuestionList = (summary) => {
  const questionBreakdownData = getProp(summary, ['question', 'breakdown', 'showcase']);
  const list = Array.isArray(questionBreakdownData)
    ? questionBreakdownData
    : getProp(questionBreakdownData, 'question');
  return (list && Array.isArray(list) && list.length > 0) ? list : [];
};

export const questionBreakdown = (summary, sections, currentQuestionIndex, deterministic = {}) => {
  const questionBreakdownData = getProp(summary, ['question', 'breakdown', 'showcase']);
  const questionList = getQuestionList(summary);
  if (questionList.length === 0) return;

  const title = getProp(questionBreakdownData, 'title');
  sections.push({
    type: 'title-main',
    content: title || 'Question-by-Question Breakdown'
  });

  const orderedList = [...questionList].sort(
    (a, b) => questionOrder(a) - questionOrder(b),
  );

  const questionsToRender = currentQuestionIndex !== undefined
    ? [{ question: orderedList[currentQuestionIndex], index: currentQuestionIndex }]
    : orderedList.map((q, i) => ({ question: q, index: i }));

  // The one place the gate is read for Question Detail. Everything downstream
  // belongs to whichever sequence was chosen here.
  const questionDetail = deterministic.enabled
    ? (s, question, index) => gatedQuestionDetail(s, question, index, deterministic)
    : legacyQuestionDetail;

  questionsToRender.forEach(({ question, index }) => {
    if (!question) return;
    questionDetail(sections, question, index);
  });
};
